import React, { useMemo, useRef, useState } from "react";
import { makeStyles, Select as MuiSelect, MenuItem, InputBase, Checkbox } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import {
  BadgeTypes,
  variantBackgroundColors,
  variantBorderColors,
  variantTextColors,
} from "../Badge/Badge";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Colors the chip using the PL Badge palette. Defaults to "blue". */
  variant?: BadgeTypes;
}

interface MultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectOption[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isSearchable?: boolean;
  /** How many chips to show in the trigger before collapsing the rest into "+N". */
  maxChips?: number;
}

const DEFAULT_VARIANT: BadgeTypes = "blue";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#364153",
    marginBottom: "2px",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "6px 8px",
    minHeight: "42px",
    borderRadius: "8px",
    border: "1px solid #E2E8F0",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    "&:focus-within": {
      borderColor: "#2B7FFF",
      boxShadow: "0 0 0 3px rgba(43, 127, 255, 0.1)",
    },
  },
  inputWrapperDisabled: {
    backgroundColor: "#F8FAFC",
    cursor: "not-allowed",
  },
  select: {
    flex: 1,
    fontSize: "16px",
    color: "#334155",
    width: "100%",
    "& .MuiSelect-select": {
      padding: 4,
      lineHeight: "1.1875",
      display: "flex",
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiSelect-icon": {
      color: "#64748B",
    },
  },
  placeholder: {
    color: "#94A3B8",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "center",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    maxWidth: "180px",
    padding: "2px 6px 2px 10px",
    borderRadius: "16px",
    border: "1px solid transparent",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: "1.4",
  },
  chipLabel: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chipRemove: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    cursor: "pointer",
    opacity: 0.7,
    transition: "opacity 0.15s ease",
    "&:hover": {
      opacity: 1,
    },
  },
  chipRemoveIcon: {
    fontSize: "15px",
  },
  chipMore: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 10px",
    borderRadius: "16px",
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: "1.4",
  },
  headerOuter: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    padding: 0,
    backgroundColor: "white",
    borderBottom: "1px solid #E2E8F0",
    outline: "none",
  },
  headerInner: {
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  searchInput: {
    fontSize: "14px",
    color: "#334155",
    width: "100%",
    "& input": {
      padding: "2px 0px",
    },
  },
  searchIcon: {
    color: "#94A3B8",
    fontSize: "20px",
  },
  selectionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  selectionCount: {
    fontSize: "13px",
    color: "#64748B",
  },
  clearButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: "#2B7FFF",
    fontSize: "13px",
    fontWeight: 600,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  clearIcon: {
    fontSize: "16px",
  },
  menuItem: {
    fontSize: "14px",
    color: "#334155",
    minHeight: "auto",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "6px",
  },
  checkbox: {
    padding: "5px",
    color: "#94A3B8",
    "&.Mui-checked": {
      color: "#2B7FFF",
    },
  },
  optionLabel: {
    whiteSpace: "normal",
  },
  noOptionsText: {
    padding: "10px 16px",
    color: "#64748B",
    fontSize: "14px",
    justifyContent: "center",
  },
}));

const MultiSelect = ({
  value,
  onChange,
  options,
  label,
  placeholder,
  disabled = false,
  className,
  isSearchable = false,
  maxChips = 3,
}: MultiSelectProps) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const optionByValue = useMemo(() => {
    const map: Record<string, MultiSelectOption> = {};
    options.forEach((opt) => {
      map[opt.value] = opt;
    });
    return map;
  }, [options]);

  const handleOpen = () => {
    if (!isSearchable) return;
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setSearchText("");
  };

  // MUI clones every direct child of Select and injects its own selection
  // onClick, so clicking non-option content (the header) pushes an `undefined`
  // value. Drop any null/undefined the menu may emit.
  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const next = (event.target.value as (string | undefined)[]).filter(
      (v): v is string => v !== undefined && v !== null
    );
    onChange(next);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const removeValue = (val: string) => {
    onChange(value.filter((v) => v !== val));
  };

  const filteredOptions = isSearchable
    ? options.filter((opt) => opt.label.toLowerCase().includes(searchText.toLowerCase()))
    : options;

  const inputWrapperClasses = [classes.inputWrapper, disabled ? classes.inputWrapperDisabled : ""]
    .filter(Boolean)
    .join(" ");

  const renderChips = (selected: string[]) => {
    if (!selected || selected.length === 0) {
      return <span className={classes.placeholder}>{placeholder}</span>;
    }

    const shown = selected.slice(0, maxChips);
    const remaining = selected.length - shown.length;

    return (
      <div className={classes.chips}>
        {shown.map((val) => {
          const opt = optionByValue[val];
          const variant = opt?.variant ?? DEFAULT_VARIANT;
          return (
            <span
              key={val}
              className={classes.chip}
              style={{
                backgroundColor: variantBackgroundColors[variant],
                borderColor: variantBorderColors[variant],
                color: variantTextColors[variant],
              }}
            >
              <span className={classes.chipLabel}>{opt?.label ?? val}</span>
              <span
                className={classes.chipRemove}
                role="button"
                aria-label={`Quitar ${opt?.label ?? val}`}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(val);
                }}
              >
                <CloseIcon className={classes.chipRemoveIcon} />
              </span>
            </span>
          );
        })}
        {remaining > 0 && <span className={classes.chipMore}>+{remaining}</span>}
      </div>
    );
  };

  return (
    <div className={[classes.container, className].filter(Boolean).join(" ")}>
      {label && <label className={classes.label}>{label}</label>}

      <div className={inputWrapperClasses}>
        <MuiSelect
          multiple
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={classes.select}
          input={<InputBase />}
          displayEmpty
          disableUnderline
          onOpen={handleOpen}
          onClose={handleClose}
          renderValue={(selected) => renderChips(selected as string[])}
          MenuProps={{
            autoFocus: false,
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          <div className={classes.headerOuter}>
            <div
              className={classes.headerInner}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {isSearchable && (
                <div className={classes.searchRow}>
                  <SearchIcon className={classes.searchIcon} />
                  <InputBase
                    inputRef={searchInputRef}
                    placeholder="Buscar..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className={classes.searchInput}
                  />
                </div>
              )}
              <div className={classes.selectionRow}>
                <span className={classes.selectionCount}>{value.length} seleccionadas</span>
                {value.length > 0 && (
                  <button type="button" className={classes.clearButton} onClick={handleClear}>
                    <CloseIcon className={classes.clearIcon} />
                    Limpiar
                  </button>
                )}
              </div>
            </div>
          </div>

          {filteredOptions.length === 0 ? (
            <MenuItem disabled className={classes.noOptionsText}>
              No se encontraron resultados
            </MenuItem>
          ) : (
            filteredOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} className={classes.menuItem}>
                <Checkbox
                  checked={value.indexOf(opt.value) > -1}
                  className={classes.checkbox}
                  disableRipple
                />
                <span className={classes.optionLabel}>{opt.label}</span>
              </MenuItem>
            ))
          )}
        </MuiSelect>
      </div>
    </div>
  );
};

export default MultiSelect;
